import { createRouter, createWebHistory } from 'vue-router'

import type { RouteRecordRaw } from 'vue-router'

import { useFeatureFlags, type FeatureFlags } from '@/composables/useFeatureFlags'

declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean
    guestOnly?: boolean
    requiresAdmin?: boolean
    requiresFeature?: keyof FeatureFlags
  }
}

const routes: RouteRecordRaw[] = [
  // Root - redirect to login or dashboard based on auth
  {
    path: '/',
    redirect: () => {
      try {
        const stored = localStorage.getItem('user')
        const isAuthenticated = stored && stored !== 'undefined' && stored !== 'null'
        return isAuthenticated ? '/brain' : '/login'
      } catch {
        return '/login'
      }
    },
  },

  // Auth routes (public)
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { requiresAuth: false, guestOnly: true },
  },
  {
    path: '/register',
    redirect: '/login',
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/views/auth/ForgotPasswordView.vue'),
    meta: { requiresAuth: false, guestOnly: true },
  },
  // Protected routes (require auth)
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('@/views/DashboardView.vue'),
    meta: { requiresAuth: true },
  },
  { path: '/data-sources', redirect: '/reports/data-sources' },
  { path: '/data-sources/upload', redirect: '/reports/data-sources/upload' },
  { path: '/data-sources/connect-database', redirect: '/reports/data-sources/connect-database' },
  { path: '/data-sources/connect-api', redirect: '/reports/data-sources/connect-api' },
  { path: '/data-sources/scrape-url', redirect: '/reports/data-sources/scrape-url' },
  { path: '/data-sources/:uuid', redirect: (to) => `/reports/data-sources/${to.params.uuid}` },
  {
    path: '/reports/data-sources',
    name: 'data-sources',
    component: () => import('@/views/DataSourcesView.vue'),
    meta: { requiresAuth: true, requiresFeature: 'reports' },
  },
  {
    path: '/reports/data-sources/upload',
    name: 'data-sources-upload',
    component: () => import('@/views/DataSourceUploadView.vue'),
    meta: { requiresAuth: true, requiresFeature: 'reports' },
  },
  {
    path: '/reports/data-sources/connect-database',
    name: 'data-sources-connect-database',
    component: () => import('@/views/DatabaseConnectionView.vue'),
    meta: { requiresAuth: true, requiresFeature: 'reports' },
  },
  {
    path: '/reports/data-sources/connect-api',
    name: 'data-sources-connect-api',
    component: () => import('@/views/ApiDataSourceView.vue'),
    meta: { requiresAuth: true, requiresFeature: 'reports' },
  },
  {
    path: '/reports/data-sources/scrape-url',
    name: 'data-sources-scrape-url',
    component: () => import('@/views/UrlDataSourceView.vue'),
    meta: { requiresAuth: true, requiresFeature: 'reports' },
  },
  {
    path: '/reports/data-sources/:uuid',
    name: 'data-source-detail',
    component: () => import('@/views/DataSourceDetailView.vue'),
    meta: { requiresAuth: true, requiresFeature: 'reports' },
  },
  {
    path: '/chat',
    name: 'chat',
    component: () => import('@/views/ChatView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/reports',
    name: 'reports',
    component: () => import('@/modules/reports/views/ReportsView.vue'),
    meta: { requiresAuth: true, requiresFeature: 'reports' },
  },
  {
    path: '/reports/create-ai',
    name: 'ai-report-builder',
    component: () => import('@/modules/reports/views/AiReportBuilderView.vue'),
    meta: { requiresAuth: true, requiresFeature: 'reports' },
  },
  {
    path: '/reports/:uuid/edit',
    name: 'report-edit',
    component: () => import('@/modules/reports/views/ReportEditView.vue'),
    meta: { requiresAuth: true, requiresFeature: 'reports' },
  },
  {
    path: '/reports/:uuid',
    name: 'report-detail',
    component: () => import('@/modules/reports/views/ReportDetailView.vue'),
    meta: { requiresAuth: true, requiresFeature: 'reports' },
  },
  {
    path: '/presentations',
    name: 'presentations',
    component: () => import('@/views/PresentationsView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/presentations/create-ai',
    name: 'ai-presentation-builder',
    component: () => import('@/views/AiPresentationBuilderView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/presentations/:uuid/edit',
    name: 'presentation-edit',
    component: () => import('@/views/PresentationEditView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/presentations/:uuid',
    name: 'presentation-detail',
    component: () => import('@/views/PresentationDetailView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/settings',
    name: 'settings',
    redirect: '/settings/password',
  },
  {
    path: '/settings/password',
    name: 'settings-password',
    component: () => import('@/views/settings/PasswordView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/settings/knowledge-base',
    name: 'settings-knowledge-base',
    component: () => import('@/views/settings/KnowledgeBaseView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/credits',
    name: 'credits',
    component: () => import('@/views/CreditsView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/credits/overage',
    name: 'credits-overage',
    component: () => import('@/views/CreditsOverageView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/credits/pricing',
    name: 'credits-pricing',
    component: () => import('@/views/CreditsPricingView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/credits/transactions/:id',
    name: 'credits-transaction',
    component: () => import('@/views/CreditTransactionView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/admin',
    name: 'admin',
    component: () => import('@/modules/admin/views/AdminView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/logs/:uuid',
    name: 'admin-log-detail',
    component: () => import('@/modules/admin/views/AdminLogDetailView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/modules',
    name: 'admin-modules',
    component: () => import('@/modules/admin/views/AdminModulesView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/audit-log',
    name: 'admin-audit-log',
    component: () => import('@/modules/admin/views/AdminAuditLogView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/notifications',
    name: 'notifications',
    component: () => import('@/views/NotificationsView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/brain',
    name: 'brain',
    component: () => import('@/modules/brain/views/BrainView.vue'),
    meta: { requiresAuth: true, requiresFeature: 'brain' },
  },
  {
    path: '/brain/sources/:notebookUuid/:sourceUuid',
    name: 'brain-source-detail',
    component: () => import('@/modules/brain/views/SourceDetailView.vue'),
    meta: { requiresAuth: true, requiresFeature: 'brain' },
  },
  {
    path: '/brain/graph',
    name: 'brain-graph',
    component: () => import('@/modules/brain/views/BrainGraphView.vue'),
    meta: { requiresAuth: true, requiresFeature: 'brain' },
  },

  // Org Chart routes
  {
    path: '/org-chart',
    name: 'org-chart',
    component: () => import('@/modules/org-chart/views/OrgChartView.vue'),
    meta: { requiresAuth: true, requiresFeature: 'org_chart' },
  },
  {
    path: '/org-chart/seats/:uuid',
    name: 'seat-detail',
    component: () => import('@/modules/org-chart/views/SeatDetailView.vue'),
    meta: { requiresAuth: true, requiresFeature: 'org_chart' },
  },
  {
    path: '/org-chart/agent-configs',
    name: 'agent-configs',
    component: () => import('@/modules/org-chart/views/AgentConfigsView.vue'),
    meta: { requiresAuth: true, requiresFeature: 'org_chart' },
  },
  {
    path: '/org-chart/approvals',
    name: 'approvals',
    component: () => import('@/modules/org-chart/views/ApprovalsView.vue'),
    meta: { requiresAuth: true, requiresFeature: 'org_chart' },
  },
  {
    path: '/org-chart/activity',
    name: 'activity-feed',
    component: () => import('@/modules/org-chart/views/ActivityFeedView.vue'),
    meta: { requiresAuth: true, requiresFeature: 'org_chart' },
  },

  // Seat Templates (admin)
  {
    path: '/admin/seat-templates',
    name: 'seat-templates',
    component: () => import('@/modules/org-chart/views/SeatTemplatesView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/seat-templates/:uuid',
    name: 'seat-template-detail',
    component: () => import('@/modules/org-chart/views/SeatTemplateDetailView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },

  // Dashboard module routes
  {
    path: '/dashboards',
    name: 'dashboards',
    component: () => import('@/modules/dashboard/views/DashboardsView.vue'),
    meta: { requiresAuth: true, requiresFeature: 'dashboard' },
  },
  {
    path: '/dashboards/builder',
    name: 'dashboard-builder',
    component: () => import('@/modules/dashboard/views/DashboardBuilderView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/dashboards/:uuid',
    name: 'dashboard-detail',
    component: () => import('@/modules/dashboard/views/DashboardDetailView.vue'),
    meta: { requiresAuth: true, requiresFeature: 'dashboard' },
  },
  {
    path: '/admin/connectors',
    name: 'connectors',
    component: () => import('@/modules/dashboard/views/ConnectorsView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/metrics',
    name: 'metrics',
    component: () => import('@/modules/dashboard/views/MetricsView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },

  // Estimation module routes
  {
    path: '/estimates',
    name: 'estimates',
    component: () => import('@/modules/estimation/views/EstimateListView.vue'),
    meta: { requiresAuth: true, requiresFeature: 'estimation' },
  },
  {
    path: '/estimates/create',
    name: 'estimate-create',
    component: () => import('@/modules/estimation/views/EstimateProjectDetailsView.vue'),
    meta: { requiresAuth: true, requiresFeature: 'estimation' },
  },
  {
    path: '/estimates/:uuid/details',
    name: 'estimate-details',
    component: () => import('@/modules/estimation/views/EstimateProjectDetailsView.vue'),
    meta: { requiresAuth: true, requiresFeature: 'estimation' },
  },
  {
    path: '/estimates/:uuid/qa',
    name: 'estimate-qa',
    component: () => import('@/modules/estimation/views/EstimateQaView.vue'),
    meta: { requiresAuth: true, requiresFeature: 'estimation' },
  },
  {
    path: '/estimates/:uuid/brief',
    name: 'estimate-brief',
    component: () => import('@/modules/estimation/views/EstimateBriefView.vue'),
    meta: { requiresAuth: true, requiresFeature: 'estimation' },
  },
  {
    path: '/estimates/:uuid/features',
    name: 'estimate-features',
    component: () => import('@/modules/estimation/views/EstimateFeaturesView.vue'),
    meta: { requiresAuth: true, requiresFeature: 'estimation' },
  },
  {
    path: '/estimates/:uuid/features/:featureUuid',
    name: 'estimate-feature-detail',
    component: () => import('@/modules/estimation/views/EstimateFeatureDetailView.vue'),
    meta: { requiresAuth: true, requiresFeature: 'estimation' },
  },
  {
    path: '/estimates/:uuid/proposal',
    name: 'estimate-proposal',
    component: () => import('@/modules/estimation/views/EstimateProposalView.vue'),
    meta: { requiresAuth: true, requiresFeature: 'estimation' },
  },

  {
    path: '/admin/workspaces',
    name: 'admin-workspaces',
    component: () => import('@/modules/admin/views/AdminWorkspacesView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },

  // Modeler module routes
  {
    path: '/modeler',
    name: 'modeler',
    component: () => import('@/modules/modeler/views/ModelerView.vue'),
    meta: { requiresAuth: true, requiresFeature: 'modeler' },
  },
  {
    path: '/modeler/:uuid',
    name: 'modeler-show',
    component: () => import('@/modules/modeler/views/ModelerShowView.vue'),
    meta: { requiresAuth: true, requiresFeature: 'modeler' },
  },

  // CRM module routes
  {
    path: '/crm/pipeline',
    name: 'crm.pipeline',
    component: () => import('@/modules/crm/views/PipelineView.vue'),
    meta: { requiresAuth: true, requiresFeature: 'crm' },
  },
  {
    path: '/crm/contacts',
    name: 'crm.contacts',
    component: () => import('@/modules/crm/views/ContactsView.vue'),
    meta: { requiresAuth: true, requiresFeature: 'crm' },
  },
  {
    path: '/crm/contacts/:uuid',
    name: 'crm.contacts.show',
    component: () => import('@/modules/crm/views/ContactDetailView.vue'),
    meta: { requiresAuth: true, requiresFeature: 'crm' },
  },
  {
    path: '/crm/companies',
    name: 'crm.companies',
    component: () => import('@/modules/crm/views/CompaniesView.vue'),
    meta: { requiresAuth: true, requiresFeature: 'crm' },
  },
  {
    path: '/crm/companies/:uuid',
    name: 'crm.companies.show',
    component: () => import('@/modules/crm/views/CompanyDetailView.vue'),
    meta: { requiresAuth: true, requiresFeature: 'crm' },
  },
  {
    path: '/crm/deals',
    name: 'crm.deals',
    component: () => import('@/modules/crm/views/DealsView.vue'),
    meta: { requiresAuth: true, requiresFeature: 'crm' },
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

const getStoredUser = () => {
  try {
    const stored = localStorage.getItem('user')
    if (!stored || stored === 'undefined' || stored === 'null') return null
    return JSON.parse(stored)
  } catch {
    localStorage.removeItem('user')
    return null
  }
}

// Global navigation guards
router.beforeEach(async (to, from, next) => {
  const user = getStoredUser()
  const isAuthenticated = user !== null

  // Redirect authenticated users away from guest-only pages
  if (to.meta.guestOnly && isAuthenticated) {
    return next('/brain')
  }

  // Redirect unauthenticated users to login
  if (to.meta.requiresAuth && !isAuthenticated) {
    return next({
      name: 'login',
      query: { redirect: to.fullPath } // Save intended destination
    })
  }

  // Check admin access
  if (to.meta.requiresAdmin && !user?.roles?.includes('admin')) {
    return next('/dashboard')
  }

  // Ensure flags are loaded for all authenticated routes
  if (isAuthenticated) {
    const { flags, loadFlags } = useFeatureFlags()
    await loadFlags()

    // Check feature flag gate
    if (to.meta.requiresFeature && !flags.value[to.meta.requiresFeature]) {
      return next('/dashboard')
    }
  }

  next()
})

// After a Vite deploy, hashed chunk filenames change and old URLs 404.
// Detect the resulting dynamic-import failure and hard-reload to the intended
// route so the browser fetches fresh HTML with the new chunk URLs.
router.onError((err, to) => {
  const isChunkError =
    err?.message?.includes('Failed to fetch dynamically imported module') ||
    err?.message?.includes('Importing a module script failed') ||
    err?.name === 'ChunkLoadError'

  if (isChunkError) {
    window.location.href = to.fullPath
  }
})

export default router
